/**
 * Authentication Context
 * Manages user authentication state, login, signup, and session
 *
 * DEVELOPMENT: Uses mock authentication with localStorage
 * PRODUCTION: Ready for API integration (just swap functions)
 */

import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { ecommerceConfig } from '../config/ecommerceConfig';

// ============================================
// TYPES
// ============================================

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
  createdAt: string;
  // Addresses
  billingAddress?: Address;
  shippingAddress?: Address;
}

export interface Address {
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  county?: string;
  country: string;
}

export interface Order {
  id: string;
  userId: string;
  orderNumber: string;
  date: string;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  items: OrderItem[];
  subtotal: number;
  deliveryFee: number;
  tax: number;
  total: number;
  shippingAddress: Address;
}

export interface OrderItem {
  productId: string;
  productName: string;
  sizeLabel: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (email: string, password: string, firstName: string, lastName: string) => Promise<void>;
  logout: () => void;
  updateProfile: (updates: Partial<User>) => Promise<void>;
  getOrders: () => Promise<Order[]>;
}

// ============================================
// CONTEXT
// ============================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'mfs-auth-user';
const ORDERS_STORAGE_KEY = 'mfs-orders';

// ============================================
// PROVIDER
// ============================================

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const savedUser = localStorage.getItem(AUTH_STORAGE_KEY);
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        console.error('Failed to parse saved user:', error);
        localStorage.removeItem(AUTH_STORAGE_KEY);
      }
    }
    setIsLoading(false);
  }, []);

  // Save user to localStorage whenever it changes
  useEffect(() => {
    if (user) {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  }, [user]);

  // ============================================
  // MOCK AUTHENTICATION (Development)
  // ============================================

  const mockLogin = async (email: string, _password: string): Promise<void> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 800));

    // Check if user exists in localStorage
    const usersKey = 'mfs-mock-users';
    const usersData = localStorage.getItem(usersKey);
    const users: User[] = usersData ? JSON.parse(usersData) : [];

    const existingUser = users.find(u => u.email === email);

    if (!existingUser) {
      throw new Error('Invalid email or password');
    }

    // In mock mode, we don't actually check password
    // In production, backend verifies credentials
    setUser(existingUser);
  };

  const mockSignup = async (
    email: string,
    _password: string,
    firstName: string,
    lastName: string
  ): Promise<void> => {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Check if user already exists
    const usersKey = 'mfs-mock-users';
    const usersData = localStorage.getItem(usersKey);
    const users: User[] = usersData ? JSON.parse(usersData) : [];

    if (users.find(u => u.email === email)) {
      throw new Error('Email already registered');
    }

    // Create new user
    const newUser: User = {
      id: `user_${Date.now()}`,
      email,
      firstName,
      lastName,
      createdAt: new Date().toISOString(),
    };

    // Save to mock database
    users.push(newUser);
    localStorage.setItem(usersKey, JSON.stringify(users));

    // Set as current user
    setUser(newUser);
  };

  // ============================================
  // PRODUCTION AUTHENTICATION (API Integration)
  // ============================================

  const productionLogin = async (email: string, password: string): Promise<void> => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Login failed');
    }

    const { user: userData, token } = await response.json();

    // Store JWT token
    localStorage.setItem('auth-token', token);

    setUser(userData);
  };

  const productionSignup = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ): Promise<void> => {
    const response = await fetch('/api/auth/signup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, firstName, lastName }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Signup failed');
    }

    const { user: userData, token } = await response.json();

    // Store JWT token
    localStorage.setItem('auth-token', token);

    setUser(userData);
  };

  // ============================================
  // PUBLIC API
  // ============================================

  const login = async (email: string, password: string): Promise<void> => {
    if (ecommerceConfig.development.testMode) {
      return mockLogin(email, password);
    }
    return productionLogin(email, password);
  };

  const signup = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
  ): Promise<void> => {
    if (ecommerceConfig.development.testMode) {
      return mockSignup(email, password, firstName, lastName);
    }
    return productionSignup(email, password, firstName, lastName);
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('auth-token');
  };

  const updateProfile = async (updates: Partial<User>): Promise<void> => {
    if (!user) throw new Error('Not authenticated');

    if (ecommerceConfig.development.testMode) {
      // Mock update
      await new Promise(resolve => setTimeout(resolve, 500));
      const updatedUser = { ...user, ...updates };
      setUser(updatedUser);

      // Update in mock database
      const usersKey = 'mfs-mock-users';
      const usersData = localStorage.getItem(usersKey);
      const users: User[] = usersData ? JSON.parse(usersData) : [];
      const index = users.findIndex(u => u.id === user.id);
      if (index !== -1) {
        users[index] = updatedUser;
        localStorage.setItem(usersKey, JSON.stringify(users));
      }
    } else {
      // Production update
      const response = await fetch('/api/user/profile', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('auth-token')}`,
        },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        throw new Error('Failed to update profile');
      }

      const updatedUser = await response.json();
      setUser(updatedUser);
    }
  };

  const getOrders = async (): Promise<Order[]> => {
    if (!user) throw new Error('Not authenticated');

    if (ecommerceConfig.development.testMode) {
      // Get mock orders from localStorage
      const ordersData = localStorage.getItem(ORDERS_STORAGE_KEY);
      const allOrders: Order[] = ordersData ? JSON.parse(ordersData) : [];
      return allOrders.filter(order => order.userId === user.id);
    } else {
      // Production: Fetch from API
      const response = await fetch('/api/user/orders', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('auth-token')}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch orders');
      }

      return response.json();
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        logout,
        updateProfile,
        getOrders,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// ============================================
// HOOK
// ============================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

// ============================================
// HELPER: Create Order (called after checkout)
// ============================================

export function createOrder(order: Order): void {
  if (!ecommerceConfig.development.testMode) {
    // Production: API handles order creation via webhook
    return;
  }

  // Mock: Save to localStorage
  const ordersData = localStorage.getItem(ORDERS_STORAGE_KEY);
  const orders: Order[] = ordersData ? JSON.parse(ordersData) : [];
  orders.push(order);
  localStorage.setItem(ORDERS_STORAGE_KEY, JSON.stringify(orders));
}
